// src/pages/ChatPage.jsx
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { UserContext } from "../Context";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "../styles/claudeTheme.css";
import "../styles/newChatPage.css";
import "../styles/settingsModal.css";
import JoinCourseModal from "../components/JoinCourseModal";
import LabSectionModal from "../components/LabSectionModal";
import PromptGuideModal from "../components/PromptGuideModal";
import WorkspaceChoiceModal from "../components/WorkspaceChoiceModal";
import MessageFeedback from "../components/MessageFeedback";
import ScaffoldingCoach from "../components/ScaffoldingCoach";
import CodeBlock from "../components/CodeBlock";
import MarkdownRenderer from "../components/MarkdownRenderer";
import {
  FiPlus,
  FiMessageSquare,
  FiGrid,
  FiUser,
  FiSend,
  FiChevronsLeft,
  FiChevronsRight,
  FiMoreVertical,
  FiMenu,
  FiTrash2,
  FiBookmark,
  FiDownload,
  FiSettings,
  FiTarget,
  FiBookOpen,
  FiCheckCircle,
  FiBook,
  FiEdit3,
  FiX,
} from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import { getApiUrl } from "../utils/api";
import AssistanceLevelSelector from "../components/AssistanceLevelSelector";

const SESSION_MODE_STORAGE_KEY = "mq_session_modes";
const CREATE_NEW_SESSION_FLAG = "mq_create_new_session_on_mount";

const WORKSPACE_MODE_COPY = {
  practice: {
    badge: "Practice",
    bannerTitle: "Practice Workspace",
    bannerHintActive: "This conversation is tagged for practice drills.",
    bannerHintInactive:
      "New chats from here will be marked for practice drills.",
    sessionHint: "Drill-style prompts and reflections live here.",
  },
  study: {
    badge: "Study Coach",
    bannerTitle: "Study Coach Workspace",
    bannerHintActive: "This conversation is tagged for guided study coaching.",
    bannerHintInactive:
      "New chats from here will be marked for study coaching.",
    sessionHint: "Plan, reflect, and capture study strategies here.",
  },
};

const WORKSPACE_MODE_ICONS = {
  practice: FiTarget,
  study: FiBookOpen,
};

const NUMBERED_ITEM_REGEX = /^\s*(\d+)[\.)]\s+(.*)$/;
const QUESTION_LABEL_REGEX = /^\s*(?:question|q)\s*(\d+)[\):\-.]?\s*(.*)$/i;

const PROMPT_GUIDE_URLS = {
  practice: process.env.REACT_APP_PRACTICE_PROMPT_GUIDE_URL || "",
  study: process.env.REACT_APP_STUDY_PROMPT_GUIDE_URL || "",
};

const DEFAULT_PROMPT_GUIDE_URL =
  process.env.REACT_APP_DEFAULT_PROMPT_GUIDE_URL ||
  "https://go.ncsu.edu/prompting-guide";

// Loading “thinking” stages: we show ONE message at a time, but we advance
// through stages and randomize the message within each stage.
const THINKING_STAGES = [
  {
    stage: "Decomposing Problem",
    messages: [
      "Reading your query",
      "Figuring out what you need",
      "Identifying the key details",
      "Understanding the question",
      "Spotting what to focus on",
    ],
  },
  {
    stage: "Breaking Down Your Query",
    messages: [
      "Breaking it into steps",
      "Choosing an approach",
      "Planning the next steps",
      "Organizing the problem",
    ],
  },
  {
    stage: "Tackling Each Step",
    messages: [
      "Working through the steps",
      "Connecting the logic",
      "Putting together an example",
    ],
  },
  {
    stage: "Retrieving from Your Course Materials",
    messages: [
      "Checking your course context",
      "Matching course terminology",
      "Recalling related concepts",
      "Connecting to course materials",
    ],
  },
  {
    stage: "Putting Everything Together",
    messages: [
      "Putting everything together",
      "Writing up a clear response",
      "Polishing the explanation",
      "Double-checking details",
      "Getting the final answer ready",
    ],
  },
];

const pickRandom = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items[Math.floor(Math.random() * items.length)];
};

const messageForStageIndex = (index) => {
  const stage = THINKING_STAGES[index % THINKING_STAGES.length];
  return pickRandom(stage?.messages);
};

// ThinkingIndicator component: dots + single line message
const ThinkingIndicator = ({ message }) => (
  <div className="thinking-indicator" aria-live="polite" aria-busy="true">
    <div className="thinking-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
    <div className="thinking-message">{message}</div>
  </div>
);

const normalizeQuestionText = (text) =>
  typeof text === "string"
    ? text
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
    : "";

const normalizeMessageText = (text) => {
  if (typeof text !== "string") {
    return text;
  }
  // Convert JSON-escaped characters to their actual characters
  return text
    .replace(/\\r\\n/g, "\n")  // Windows line endings first
    .replace(/\\n/g, "\n")      // Line feeds
    .replace(/\\t/g, "\t")      // Tabs
    .replace(/\\\\/g, "\\");    // Escaped backslashes
};

const normalizeChatHistory = (history) =>
  Array.isArray(history)
    ? history.map((entry) =>
        entry && typeof entry === "object"
          ? { ...entry, text: normalizeMessageText(entry.text) }
          : entry
      )
    : [];

const extractPracticeQuestions = (text) => {
  if (typeof text !== "string") {
    return [];
  }

  const lines = text.split(/\r?\n/);
  const questions = [];
  let current = null;

  lines.forEach((line) => {
    if (!line || line.trim().length === 0) {
      if (current) {
        questions.push(current);
        current = null;
      }
      return;
    }

    const trimmed = line.trim();
    const numberedMatch = trimmed.match(NUMBERED_ITEM_REGEX);
    const labeledMatch = trimmed.match(QUESTION_LABEL_REGEX);

    if (numberedMatch || labeledMatch) {
      if (current) {
        questions.push(current);
      }

      const match = numberedMatch || labeledMatch;
      const number = parseInt(match[1], 10);
      const body = match[2] ? match[2].trim() : "";

      if (!Number.isNaN(number) && body) {
        current = {
          number,
          text: body,
          rawLine: line,
        };
      } else {
        current = null;
      }
      return;
    }

    if (current && /^\s{2,}/.test(line)) {
      current.text = `${current.text} ${trimmed}`.trim();
    } else if (current) {
      questions.push(current);
      current = null;
    }
  });

  if (current) {
    questions.push(current);
  }

  const seenNumbers = new Set();
  const results = [];

  questions.forEach((question) => {
    const number = parseInt(question.number, 10);
    if (Number.isNaN(number) || seenNumbers.has(number)) {
      return;
    }
    seenNumbers.add(number);
    const textValue = question.text ? question.text.trim() : "";
    if (textValue) {
      results.push({
        number,
        text: textValue,
        rawLine: question.rawLine,
      });
    }
  });

  return results;
};

const extractSequenceNumber = (message) => {
  if (!message) return null;
  const candidate =
    message.sequenceNum ??
    message.sequence_num ??
    message.sequence_number ??
    message.sequence;
  const parsed = parseInt(candidate, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const SessionMenu = ({ session, onPin, onDelete, onDownload, onIsPinned }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isPinned = onIsPinned(session);

  return (
    <div
      className={`session-menu-container ${isOpen ? "open" : ""}`}
      ref={menuRef}
    >
      <button onClick={handleToggle} className="session-menu-button">
        <FiMoreVertical />
      </button>
      {isOpen && (
        <div className="session-menu">
          <div className="session-menu-item" onClick={() => onPin(session)}>
            <FiBookmark />
            <span>{isPinned ? "Unpin" : "Pin"}</span>
          </div>
          <div className="session-menu-item" onClick={() => onDelete(session)}>
            <FiTrash2 />
            <span>Delete</span>
          </div>
          <div
            className="session-menu-item"
            onClick={() => onDownload(session)}
          >
            <FiDownload />
            <span>Download</span>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsModal = ({ isVisible, onClose, user }) => {
  const [activeTab, setActiveTab] = useState("account");
  const [formData, setFormData] = useState({
    fullName: user?.full_name || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      if (
        formData.newPassword &&
        formData.newPassword !== formData.confirmPassword
      ) {
        setMessage({ text: "New passwords do not match", type: "error" });
        setIsLoading(false);
        return;
      }

      const updateData = {
        full_name: formData.fullName,
        email: formData.email,
      };

      if (formData.newPassword) {
        updateData.current_password = formData.currentPassword;
        updateData.new_password = formData.newPassword;
      }

      const response = await fetch(getApiUrl("/api/user/update-profile"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        setMessage({ text: "Profile updated successfully", type: "success" });
        setFormData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
      } else {
        setMessage({
          text: result.message || "Failed to update profile",
          type: "error",
        });
      }
    } catch (error) {
      setMessage({
        text: "An error occurred while updating profile",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-sidebar">
          <div className="settings-tabs">
            <button
              className={`tab-button ${
                activeTab === "account" ? "active" : ""
              }`}
              onClick={() => setActiveTab("account")}
            >
              <FiUser />
              <span>Account</span>
            </button>
          </div>
        </div>
        <div className="settings-content-container">
          <div className="settings-content-header">
            <h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h2>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="settings-content">
            {activeTab === "account" && (
              <form onSubmit={handleSubmit} className="account-form">
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="form-section-title">Change Password</h3>
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={onClose}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="save-button"
                  >
                    {isLoading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatPage = () => {
  const { user, setUser, logout, refreshUser } = useContext(UserContext);
  const { courseNumber: urlCourseNumber, courseName: urlCourseName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSessionVerified, setIsSessionVerified] = useState(false);
  const authRedirectRef = useRef(false);

  const [studyFeaturesEnabled, setStudyFeaturesEnabled] = useState(true);
  const [studyFeaturesLoaded, setStudyFeaturesLoaded] = useState(false);

  const workspaceMode = useMemo(() => {
    const path = location.pathname || "";
    if (path.startsWith("/student/practice")) return "practice";
    if (path.startsWith("/student/study")) return "study";
    return null;
  }, [location.pathname]);

  const handleUnauthorizedAccess = useCallback(() => {
    setIsSessionVerified(false);
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("user");
      window.sessionStorage.removeItem("ta_manager_mode");
    }
    if (typeof setUser === "function") {
      setUser(null);
    }
    if (!authRedirectRef.current) {
      authRedirectRef.current = true;
      navigate("/login");
    }
  }, [navigate, setUser]);

  const [sessionModes, setSessionModes] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const stored = window.localStorage.getItem(SESSION_MODE_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      console.warn("Failed to read session mode metadata", err);
      return {};
    }
  });

  const sessionModesRef = useRef(sessionModes);
  useEffect(() => {
    sessionModesRef.current = sessionModes;
  }, [sessionModes]);

  const persistSessionModes = useCallback((next) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SESSION_MODE_STORAGE_KEY,
        JSON.stringify(next)
      );
    } catch (err) {
      console.warn("Failed to persist session mode metadata", err);
    }
  }, []);

  const resolveSessionMode = useCallback(
    (session) => {
      if (!session) return null;
      const modeValue =
        session.mode || (session.id ? sessionModes[session.id] : null) || null;
      return typeof modeValue === "string" ? modeValue.toLowerCase() : null;
    },
    [sessionModes]
  );

  const updateSessionMode = useCallback(
    (sessionId, mode) => {
      if (!sessionId || !mode) return;
      if (studyFeaturesLoaded && !studyFeaturesEnabled) return;
      const normalized =
        typeof mode === "string" ? mode.toLowerCase() : mode;
      if (!normalized || !WORKSPACE_MODE_COPY[normalized]) return;

      setSessionModes((prev) => {
        if (prev[sessionId] === normalized) {
          return prev;
        }
        const next = { ...prev, [sessionId]: normalized };
        sessionModesRef.current = next;
        persistSessionModes(next);
        return next;
      });
    },
    [persistSessionModes, studyFeaturesEnabled, studyFeaturesLoaded]
  );

  const clearSessionMode = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      setSessionModes((prev) => {
        if (!prev[sessionId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[sessionId];
        sessionModesRef.current = next;
        persistSessionModes(next);
        return next;
      });
    },
    [persistSessionModes]
  );

  const renameSessionMode = useCallback(
    (oldId, newId) => {
      if (!oldId || !newId || oldId === newId) return;
      setSessionModes((prev) => {
        const existing = prev[oldId];
        if (!existing) {
          return prev;
        }
        const next = { ...prev };
        delete next[oldId];
        next[newId] = existing;
        sessionModesRef.current = next;
        persistSessionModes(next);
        return next;
      });
    },
    [persistSessionModes]
  );

  // Get course from URL params or localStorage (for /student/practice and /student/study routes)
  const courseName = useMemo(() => {
    if (urlCourseName) return urlCourseName;
    try {
      const stored = localStorage.getItem("currentCourse");
      if (stored) {
        const course = JSON.parse(stored);
        return course.course_name;
      }
    } catch (e) {
      console.error("Failed to parse currentCourse from localStorage", e);
    }
    return null;
  }, [urlCourseName]);

  const courseNumber = useMemo(() => {
    if (urlCourseNumber) return urlCourseNumber;
    try {
      const stored = localStorage.getItem("currentCourse");
      if (stored) {
        const course = JSON.parse(stored);
        return course.course_number;
      }
    } catch (e) {
      console.error("Failed to parse currentCourse from localStorage", e);
    }
    return null;
  }, [urlCourseNumber]);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      if (!courseName || !courseNumber) {
        return;
      }
      try {
        const response = await fetch(getApiUrl("/api/course/feature-flags/get"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            course_name: courseName,
            course_number: courseNumber,
          }),
        });

        if (!response.ok) {
          setStudyFeaturesEnabled(true);
          setStudyFeaturesLoaded(true);
          return;
        }

        const data = await response.json();
        const flags = data?.feature_flags;
        setStudyFeaturesEnabled(
          typeof flags?.study_features_enabled === "boolean"
            ? flags.study_features_enabled
            : true
        );
        setStudyFeaturesLoaded(true);
      } catch (err) {
        console.warn("Failed to load course feature flags", err);
        setStudyFeaturesEnabled(true);
        setStudyFeaturesLoaded(true);
      }
    };

    fetchFeatureFlags();
  }, [courseName, courseNumber]);

  useEffect(() => {
    if (!studyFeaturesLoaded) return;
    if (studyFeaturesEnabled) return;
    if (workspaceMode && courseNumber && courseName) {
      navigate(`/sessions/${courseNumber}/${courseName}`, { replace: true });
    }
  }, [studyFeaturesEnabled, studyFeaturesLoaded, workspaceMode, courseNumber, courseName, navigate]);

  // Data loading states
  const [dataLoadingStates, setDataLoadingStates] = useState({
    user: false,
    courses: false,
    agent: false,
    sessions: false,
  });

  // UI state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return typeof window !== "undefined" ? window.innerWidth <= 768 : true;
  });
  const [searchQuery, setSearchQuery] = useState("");

  // Course navigation state
  const [allCourses, setAllCourses] = useState([]);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [isShowingCourseSettings, setIsShowingCourseSettings] = useState(false);
  const [showLabSectionModal, setShowLabSectionModal] = useState(false);
  const [pendingWorkspaceNavigation, setPendingWorkspaceNavigation] = useState(null);

  // Workspace unlock state
  const UNLOCK_CODE = "STUDY2025"; // Shared unlock code for all students
  const [workspacesUnlocked, setWorkspacesUnlocked] = useState(() => {
    try {
      const stored = localStorage.getItem("mq_workspaces_unlocked");
      return stored === "true";
    } catch (e) {
      return false;
    }
  });
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockInput, setUnlockInput] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [workspaceChoice, setWorkspaceChoice] = useState(null);

  // Chat & session state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [olderSessions, setOlderSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [currentSessionName, setCurrentSessionName] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [pinnedSessions, setPinnedSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);
  const [thinkingStageIndex, setThinkingStageIndex] = useState(0);
  const [thinkingMessage, setThinkingMessage] = useState(() => messageForStageIndex(0));
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionsInitialized, setSessionsInitialized] = useState(false);

  // GenAI Assistance Level state (1-5 scale)
  const [assistanceLevel, setAssistanceLevel] = useState(1); // Default to Idea Spark
  const [assistanceLevelMeta, setAssistanceLevelMeta] = useState({
    1: { name: "Idea Spark", description: "Concepts and hints only, no code" },
    2: { name: "Code Check", description: "Review your code, no rewrites" },
    3: { name: "Debug Helper", description: "Trace errors, you fix them" },
    4: { name: "Co-Builder", description: "Draft snippets together" },
    5: { name: "AI Only", description: "Complete solutions (for learning only)" },
  });

  const [isProfileDropdownVisible, setIsProfileDropdownVisible] =
    useState(false);

  const activeSessionData = useMemo(() => {
    if (!activeSession) return null;
    return (
      [...sessions, ...pinnedSessions].find((s) => s.id === activeSession) ||
      null
    );
  }, [activeSession, sessions, pinnedSessions]);

  const activeSessionId = activeSessionData?.id || null;
  const activeSessionMode = activeSessionId
    ? resolveSessionMode(activeSessionData)
    : null;

  const activeSessionWorkspace = useMemo(() => {
    if (!activeSessionData?.workspace_mode) return null;
    const raw = activeSessionData.workspace_mode;
    return typeof raw === "string" ? raw.toLowerCase() : null;
  }, [activeSessionData]);

  // DO NOT auto-tag existing sessions when switching workspace modes
  // Sessions should only be tagged when created in that workspace
  const normalizedWorkspaceMode = workspaceMode
    ? workspaceMode.toLowerCase()
    : null;

  const practiceUIEnabled = useMemo(() => {
    // Hidden for now - "Add to Practice Log" feature disabled
    return false;
    // if (activeSessionMode === "practice") return true;
    // if (activeSessionWorkspace === "practice") return true;
    // return normalizedWorkspaceMode === "practice";
  }, [activeSessionMode, activeSessionWorkspace, normalizedWorkspaceMode]);

  const practiceExperimentData = activeSessionData?.experiment_data || null;
  const practiceCondition = practiceExperimentData?.condition || null;

  const acceptedPracticeInfo = useMemo(() => {
    const list = Array.isArray(practiceExperimentData?.accepted_questions)
      ? practiceExperimentData.accepted_questions
      : [];
    const pairSet = new Set();
    const textSet = new Set();

    list.forEach((item) => {
      const normalizedNumber = parseInt(item?.question_number, 10);
      const sequenceCandidate =
        item?.response_sequence_num ??
        item?.sequence_num ??
        item?.response_sequence;
      const normalizedSequence = parseInt(sequenceCandidate, 10);
      
      // Track the specific pair of (sequence, questionNumber)
      if (!Number.isNaN(normalizedSequence) && !Number.isNaN(normalizedNumber)) {
        pairSet.add(`${normalizedSequence}-${normalizedNumber}`);
      }
      
      const normalizedText = normalizeQuestionText(item?.question_text);
      if (normalizedText) {
        textSet.add(normalizedText);
      }
    });

    return {
      list,
      pairSet,
      textSet,
    };
  }, [practiceExperimentData]);

  const [acceptingQuestionKey, setAcceptingQuestionKey] = useState(null);
  const [practiceActionError, setPracticeActionError] = useState(null);
  const acceptedQuestionPairs = acceptedPracticeInfo.pairSet;
  const acceptedQuestionTexts = acceptedPracticeInfo.textSet;
  const practiceErrorSequenceKey = practiceActionError?.sequenceKey ?? null;
  const practiceErrorMessage = practiceActionError?.message || null;
  const [showPromptGuideModal, setShowPromptGuideModal] = useState(false);
  const [hasPromptGuideAutoPrompted, setHasPromptGuideAutoPrompted] =
    useState(false);

  const isConditionA = useMemo(() => {
    const condition = user?.experiment_condition;
    return typeof condition === "string" && condition.toUpperCase() === "A";
  }, [user?.experiment_condition]);

  const promptGuideMode = useMemo(() => {
    const candidates = [
      normalizedWorkspaceMode,
      typeof activeSessionMode === "string"
        ? activeSessionMode.toLowerCase()
        : null,
      typeof activeSessionWorkspace === "string"
        ? activeSessionWorkspace.toLowerCase()
        : null,
    ];
    return (
      candidates.find((mode) => mode === "practice" || mode === "study") ||
      null
    );
  }, [normalizedWorkspaceMode, activeSessionMode, activeSessionWorkspace]);

  const promptGuideStorageKey = useMemo(() => {
    if (!isConditionA || !promptGuideMode || !user?.username) {
      return null;
    }
    return `mq_prompt_guide_seen_${user.username}_${promptGuideMode}`;
  }, [isConditionA, promptGuideMode, user?.username]);

  const promptGuideUrl = useMemo(() => {
    if (!promptGuideMode) {
      return "";
    }
    return PROMPT_GUIDE_URLS[promptGuideMode] || DEFAULT_PROMPT_GUIDE_URL || "";
  }, [promptGuideMode]);

  const showPromptGuideAccess = isConditionA && Boolean(promptGuideMode);
  const showAssistanceLevel = !workspaceMode && !activeSessionMode;

  const getLatestSessionForWorkspace = useCallback(
    (mode) => {
      const normalized =
        typeof mode === "string" ? mode.toLowerCase() : "";
      if (!normalized) {
        return null;
      }

      const allSessions = [...pinnedSessions, ...sessions];
      if (!allSessions.length) {
        return null;
      }

      const filtered = allSessions.filter((session) => {
        if (!session) return false;
        const workspaceValue =
          typeof session.workspace_mode === "string"
            ? session.workspace_mode.toLowerCase()
            : null;
        const resolved = resolveSessionMode(session);
        return (
          workspaceValue === normalized || (resolved && resolved === normalized)
        );
      });

      if (!filtered.length) {
        return null;
      }

      const getTimestamp = (session) => {
        const raw =
          session.last_message_date ||
          (Array.isArray(session.chat_history) &&
          session.chat_history.length > 0
            ? session.chat_history[session.chat_history.length - 1]
                ?.date_and_time
            : null) ||
          session.date_and_time ||
          null;
        if (!raw) return 0;
        const value = new Date(raw).getTime();
        return Number.isFinite(value) ? value : 0;
      };

      filtered.sort((a, b) => getTimestamp(b) - getTimestamp(a));
      return filtered[0] || null;
    },
    [pinnedSessions, sessions, resolveSessionMode]
  );

  useEffect(() => {
    setHasPromptGuideAutoPrompted(false);
  }, [promptGuideStorageKey]);

  useEffect(() => {
    if (
      !showPromptGuideAccess ||
      !promptGuideStorageKey ||
      hasPromptGuideAutoPrompted
    ) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const alreadySeen =
      window.localStorage.getItem(promptGuideStorageKey) === "true";
    if (!alreadySeen) {
      setShowPromptGuideModal(true);
    }
    setHasPromptGuideAutoPrompted(true);
  }, [
    showPromptGuideAccess,
    promptGuideStorageKey,
    hasPromptGuideAutoPrompted,
  ]);

  const markPromptGuideSeen = useCallback(() => {
    if (!promptGuideStorageKey || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(promptGuideStorageKey, "true");
  }, [promptGuideStorageKey]);

  const handlePromptGuideClose = useCallback(() => {
    markPromptGuideSeen();
    setShowPromptGuideModal(false);
  }, [markPromptGuideSeen]);

  const handlePromptGuideButtonClick = useCallback(() => {
    setShowPromptGuideModal(true);
  }, []);

  const handlePromptGuideOpenDoc = useCallback(() => {
    if (promptGuideUrl) {
      window.open(promptGuideUrl, "_blank", "noopener,noreferrer");
    }
    markPromptGuideSeen();
    setShowPromptGuideModal(false);
  }, [promptGuideUrl, markPromptGuideSeen]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const profileDropdownHideTimeout = useRef(null);
  const courseDropdownHideTimeout = useRef(null);

  // Refs
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setPracticeActionError(null);
  }, [activeSessionId, messages.length]);

  useEffect(() => {
    if (!practiceActionError) return undefined;
    const timer = setTimeout(() => setPracticeActionError(null), 5000);
    return () => clearTimeout(timer);
  }, [practiceActionError]);

  // Helper functions for data checking
  const isUserDataValid = useCallback(() => {
    return user && user.full_name && user.email;
  }, [user]);

  const isCoursesDataValid = useCallback(() => {
    return Array.isArray(allCourses) && allCourses.length >= 0; // Allow empty array
  }, [allCourses]);

  const isAgentDataValid = useCallback(() => {
    return selectedAgent && selectedAgent.config_name;
  }, [selectedAgent]);

  const isSessionsDataValid = useCallback(() => {
    return Array.isArray(sessions) && Array.isArray(pinnedSessions);
  }, [sessions, pinnedSessions]);

  const getActiveSessionForCourse = (course) => {
    try {
      const activeSessions =
        JSON.parse(localStorage.getItem("activeSessions")) || {};
      return activeSessions[course];
    } catch (e) {
      console.error("Failed to read active session from localStorage", e);
      return null;
    }
  };

  const setActiveSessionForCourse = useCallback((course, sessionId) => {
    try {
      const activeSessions =
        JSON.parse(localStorage.getItem("activeSessions")) || {};
      activeSessions[course] = sessionId;
      localStorage.setItem("activeSessions", JSON.stringify(activeSessions));
    } catch (e) {
      console.error("Failed to save active session to localStorage", e);
    }
  }, []);

  // Enhanced API call helper
  const apiCall = useCallback(async (path, bodyObj) => {
    const tryPaths = path.startsWith("/api/")
      ? [getApiUrl(path), getApiUrl(path.replace("/api", ""))]
      : [getApiUrl(path), getApiUrl("/api" + path)];
    const headers = { "Content-Type": "application/json" };
    for (let p of tryPaths) {
      try {
        const res = await fetch(p, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify(bodyObj || {}),
        });
        if (res.status === 404) {
          continue;
        }
        let data = null;
        try {
          data = await res.json();
        } catch (_) {}
        if (res.status === 401 || res.status === 403) {
          handleUnauthorizedAccess();
          return { ok: false, status: res.status, data };
        }
        return { ok: res.ok, status: res.status, data };
      } catch (e) {
        continue;
      }
    }
    return { ok: false, status: 0, data: null };
  }, [handleUnauthorizedAccess]);

  // Enhanced data fetching functions with loading states
  const fetchUserData = useCallback(async () => {
    if (dataLoadingStates.user) {
      return false;
    }

    setDataLoadingStates((prev) => ({ ...prev, user: true }));

    try {
      if (refreshUser) {
        const updatedUser = await refreshUser();
        if (!updatedUser) {
          handleUnauthorizedAccess();
          return false;
        }
        setIsSessionVerified(true);
        authRedirectRef.current = false;
        return true;
      }
    } catch (error) {
      handleUnauthorizedAccess();
      return false;
    } finally {
      setDataLoadingStates((prev) => ({ ...prev, user: false }));
    }

    return false;
  }, [refreshUser, dataLoadingStates.user, handleUnauthorizedAccess]);

  // While loading, advance through stages and randomize the message within each stage.
  // Show ONE message at a time.
  useEffect(() => {
    if (!loading) {
      setThinkingStageIndex(0);
      setThinkingMessage(messageForStageIndex(0));
      return;
    }

    setThinkingStageIndex(0);
    setThinkingMessage(messageForStageIndex(0));

    const interval = setInterval(() => {
      setThinkingStageIndex((prev) => {
        const lastIndex = THINKING_STAGES.length - 1;
        if (prev >= lastIndex) {
          // Stay on the final stage if generation is still ongoing.
          return prev;
        }
        const next = prev + 1;
        setThinkingMessage(messageForStageIndex(next));
        return next;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [loading]);

  const fetchAllCourses = useCallback(async () => {
    if (!isSessionVerified || dataLoadingStates.courses) return; 

    setDataLoadingStates((prev) => ({ ...prev, courses: true }));

    try {
      const response = await fetch(getApiUrl("/api/course/get"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (response.status === 401 || response.status === 403) {
        handleUnauthorizedAccess();
        return;
      }
      const data = await response.json();
      if (data.status === "success" && Array.isArray(data.courses)) {
        setAllCourses(data.courses);
      } else {
        setAllCourses([]);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      setAllCourses([]);
    } finally {
      setDataLoadingStates((prev) => ({ ...prev, courses: false }));
    }
  }, [isSessionVerified, dataLoadingStates.courses, handleUnauthorizedAccess]);

  const fetchAgentConfiguration = useCallback(async () => {
    if (!courseName || !user || dataLoadingStates.agent || !isSessionVerified) {
      return;
    }

    setDataLoadingStates((prev) => ({ ...prev, agent: true }));

    try {
      const response = await fetch(getApiUrl("/api/agent/get/all"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ course_name: courseName }),
      });
      if (response.status === 401 || response.status === 403) {
        handleUnauthorizedAccess();
        return;
      }
      const data = await response.json();

      const agentData = data.data || data;
      let agentConfig = null;
      if (Array.isArray(agentData) && agentData.length > 0) {
        agentConfig = agentData[0];
      } else {
        agentConfig = {
          config_name: "default",
          name: "Default Agent",
        };
      }

      setSelectedAgent(agentConfig);
    } catch (err) {
      const fallbackAgent = {
        config_name: "default",
        name: "Default Agent",
      };
      setSelectedAgent(fallbackAgent);
    } finally {
      setDataLoadingStates((prev) => ({ ...prev, agent: false }));
    }
  }, [courseName, user, dataLoadingStates.agent, isSessionVerified, handleUnauthorizedAccess]);

  const fetchSessions = useCallback(async () => {
    if (
      !user ||
      !courseName ||
      !selectedAgent ||
      dataLoadingStates.sessions ||
      !isSessionVerified
    ) {
      return;
    }

    setDataLoadingStates((prev) => ({ ...prev, sessions: true }));

    try {
      const res = await apiCall("/api/session/get/all", {
        course_name: courseName,
        config_name: selectedAgent.config_name,
      });

      if (!res.ok || !res.data) {
        setSessions([]);
        setPinnedSessions([]);
        setDataLoadingStates((prev) => ({ ...prev, sessions: false }));
        return;
      }

      const raw = res.data.data || res.data;

      if (!Array.isArray(raw) || raw.length === 0) {
        setSessions([]);
        setPinnedSessions([]);
        setDataLoadingStates((prev) => ({ ...prev, sessions: false }));
        return;
      }

      const sorted = raw
        .map((s) => {
          const sessionId = s.session_name;
          const persistedMode = sessionModesRef.current[sessionId];
          const backendMode =
            typeof s.workspace_mode === "string"
              ? s.workspace_mode.toLowerCase()
              : typeof s.mode === "string"
              ? s.mode.toLowerCase()
              : null;
          const normalizedMode =
            backendMode ||
            (typeof persistedMode === "string"
              ? persistedMode.toLowerCase()
              : null);

          return {
            id: sessionId,
            name: sessionId,
            owner: s.session_owner,
            last_message_date:
              s.chat_history?.length > 0
                ? s.chat_history[s.chat_history.length - 1].date_and_time
                : s.date_and_time,
            chat_history: s.chat_history || [],
            is_pinned: s.is_pinned || false,
            _id: s._id,
            mode: normalizedMode,
            workspace_mode: s.workspace_mode || normalizedMode || null,
            experiment_data: s.experiment_data || null,
          };
        })
        .sort(
          (a, b) =>
            new Date(b.last_message_date) - new Date(a.last_message_date)
        );

      const pinned = sorted.filter((s) => s.is_pinned);
      const others = sorted.filter((s) => !s.is_pinned);

      if (sorted.length > 0) {
        const nextModes = { ...sessionModesRef.current };
        let modesChanged = false;
        sorted.forEach((sessionItem) => {
          if (
            sessionItem.mode &&
            WORKSPACE_MODE_COPY[sessionItem.mode] &&
            nextModes[sessionItem.id] !== sessionItem.mode
          ) {
            nextModes[sessionItem.id] = sessionItem.mode;
            modesChanged = true;
          }
        });
        if (modesChanged) {
          sessionModesRef.current = nextModes;
          setSessionModes(nextModes);
          persistSessionModes(nextModes);
        }
      }

      setPinnedSessions(pinned);
      setSessions(others);

      // Set active session
      const lastActiveSessionId = getActiveSessionForCourse(courseName);
      if (
        lastActiveSessionId &&
        sorted.some((s) => s.id === lastActiveSessionId)
      ) {
        setActiveSession(lastActiveSessionId);
      } else if (sorted.length > 0) {
        setActiveSession(sorted[0].id);
        setActiveSessionForCourse(courseName, sorted[0].id);
      }

    } catch (error) {
      console.error("Error fetching sessions:", error);
      setSessions([]);
      setPinnedSessions([]);
    } finally {
      setSessionsInitialized(true);
      setDataLoadingStates((prev) => ({ ...prev, sessions: false }));
    }
  }, [
    user,
    courseName,
    selectedAgent,
    apiCall,
    dataLoadingStates.sessions,
    persistSessionModes,
    isSessionVerified,
  ]);

  // Comprehensive data check and refetch function - simplified for initial load
  const checkAndRefetchData = useCallback(async () => {
    let sessionValid = isSessionVerified;

    if (!sessionValid || !user || !user.full_name) {
      sessionValid = await fetchUserData();
    }

    if (!sessionValid) {
      return;
    }

    if (!allCourses.length) {
      await fetchAllCourses();
    }

    if (courseName && user && !selectedAgent) {
      await fetchAgentConfiguration();
    }

    if (
      courseName &&
      user &&
      selectedAgent &&
      sessions.length === 0 &&
      pinnedSessions.length === 0
    ) {
      await fetchSessions();
    }
  }, [
    isSessionVerified,
    user,
    allCourses.length,
    selectedAgent,
    sessions.length,
    pinnedSessions.length,
    fetchUserData,
    fetchAllCourses,
    fetchAgentConfiguration,
    fetchSessions,
    courseName,
  ]);

  // Reset state when course changes
  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setActiveSession(null);
    setSessions([]);
    setRecentSessions([]);
    setOlderSessions([]);
    setPinnedSessions([]);
    setLoading(false);
    setInput("");
    setSelectedAgent(null);
    setAssistanceLevel(1); // Reset to Idea Spark
    setIsShowingCourseSettings(false);
    setIsCreatingSession(false);
    setSessionsInitialized(false);

    // Reset data loading states
    setDataLoadingStates({
      user: false,
      courses: false,
      agent: false,
      sessions: false,
    });
  }, [courseName, courseNumber]);

  // Main effect to check and refetch data on mount and when dependencies change
  useEffect(() => {
    checkAndRefetchData();
  }, [checkAndRefetchData]);

  // Periodic data integrity check
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        checkAndRefetchData();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [checkAndRefetchData]);

  // Handle visibility change to refetch data when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAndRefetchData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [checkAndRefetchData]);

  const handleCreateNewSession = useCallback(
    async (isFirstSession = false) => {
      if (
        !user ||
        !courseName ||
        !selectedAgent ||
        isCreatingSession ||
        !isSessionVerified
      ) {
        return null;
      }

      setIsCreatingSession(true);

      try {
        // Generate mode-specific session name
        let sessionNamePrefix = "New Conversation";
        if (normalizedWorkspaceMode === "practice") {
          sessionNamePrefix = "New Practice Question Set";
        } else if (normalizedWorkspaceMode === "study") {
          sessionNamePrefix = "New Study Plan";
        }
        const sessionName = `${sessionNamePrefix} ${uuidv4()}`;

        const createRes = await apiCall("/api/session/create", {
          session_name: sessionName,
          config_name: selectedAgent.config_name,
          course_name: courseName,
          course_number: courseNumber,
          workspace_mode: normalizedWorkspaceMode,
        });

        if (
          createRes.ok &&
          createRes.data &&
          (createRes.data.session || createRes.data.data)
        ) {
          const sessionData =
            createRes.data.session || createRes.data.data || createRes.data;
          const initialMode = normalizedWorkspaceMode || sessionData.workspace_mode;
          const newSession = {
            id: sessionData._id || sessionData.session_name, // Use _id as primary identifier
            _id: sessionData._id, // Store _id explicitly
            name: sessionData.session_name,
            last_message_date: sessionData.date_and_time,
            chat_history: [],
            mode: initialMode || null,
            workspace_mode: sessionData.workspace_mode || initialMode || null,
            experiment_data: sessionData.experiment_data || null,
          };
          if (initialMode) {
            updateSessionMode(newSession.id, initialMode);
          }
          setSessions((prev) => [newSession, ...prev]);
          setActiveSession(newSession.id);
          setActiveSessionForCourse(courseName, newSession.id);
          if (isFirstSession) setMessages([]);
          return newSession;
        } else {
          console.error(
            "Failed to create session",
            createRes.status,
            createRes.data
          );
          alert("Failed to create new session. Please try again.");
          return null;
        }
      } catch (error) {
        console.error("Error in session creation:", error);
        alert("Error creating new session. Please check your connection.");
        return null;
      } finally {
        setIsCreatingSession(false);
      }
    },
    [
      user,
      courseName,
      courseNumber,
      selectedAgent,
      apiCall,
      isCreatingSession,
      normalizedWorkspaceMode,
      updateSessionMode,
      isSessionVerified,
    ]
  );

  // Helpers
  const scrollToBottom = () => {
    setTimeout(() => {
      const mainContainer = document.querySelector(".claude-main");
      if (mainContainer) {
        mainContainer.scrollTo({
          top: mainContainer.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const resizeTextarea = () => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsSidebarCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load saved assistance level from localStorage
  useEffect(() => {
    if (!courseName) return;
    try {
      const savedLevel = localStorage.getItem(`mq_assistance_level_${courseName}`);
      if (savedLevel) {
        const parsed = parseInt(savedLevel, 10);
        if (parsed >= 1 && parsed <= 5) {
          setAssistanceLevel(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to load assistance level", e);
    }
  }, [courseName]);

  // Session filtering for search
  const [isSearching, setIsSearching] = useState(false);

  // Check for flag to create new session after navigation
  useEffect(() => {
    if (!user || !courseName || !selectedAgent) return;
    
    // Check if we should create a new session after navigation
    const shouldCreateNew = typeof window !== "undefined" 
      ? window.localStorage.getItem(CREATE_NEW_SESSION_FLAG) === "true"
      : false;
    
    if (shouldCreateNew) {
      // Clear the flag
      window.localStorage.removeItem(CREATE_NEW_SESSION_FLAG);
      
      // Wait a bit for sessions to load, then create new session
      setTimeout(() => {
        handleCreateNewSession(false);
      }, 500);
    }
  }, [user, courseName, selectedAgent, handleCreateNewSession]);

  useEffect(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const unpinnedSessions = sessions.filter(
      (s) => !pinnedSessions.some((ps) => ps.id === s.id)
    );
    const filtered = unpinnedSessions.filter((s) =>
      (s.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
    const recents = [];
    const oldies = [];
    filtered.forEach((s) => {
      if (new Date(s.last_message_date) > oneWeekAgo) {
        recents.push(s);
      } else {
        oldies.push(s);
      }
    });
    setRecentSessions(recents);
    setOlderSessions(oldies);
  }, [searchQuery, sessions, pinnedSessions]);

  const handleSessionSelect = useCallback(
    async (sessionId) => {
      const session = [...sessions, ...pinnedSessions].find(
        (s) => s.id === sessionId
      );

      if (
        session &&
        (!session.chat_history || session.chat_history.length === 0)
      ) {
        try {
          const individualRes = await apiCall("/api/session/get/one", {
            session_id: sessionId, // Use session_id instead of session_name
          });
          if (individualRes.ok && individualRes.data) {
            const updatedHistory = individualRes.data.chat_history || [];

            const updateSessionWithHistory = (s) =>
              s.id === sessionId ? { ...s, chat_history: updatedHistory } : s;

            setSessions((prev) => prev.map(updateSessionWithHistory));
            setPinnedSessions((prev) => prev.map(updateSessionWithHistory));

            setMessages(normalizeChatHistory(updatedHistory));
          }
        } catch (error) {
          console.error("Failed to fetch individual session:", error);
        }
      }

      setActiveSession(sessionId);
      setActiveSessionForCourse(courseName, sessionId);
      setTimeout(() => scrollToBottom(), 100);
    },
    [courseName, sessions, pinnedSessions, apiCall]
  );

  // Load messages when activeSession changes
  useEffect(() => {
    if (!activeSession) {
      setMessages([]);
      setCurrentSessionName(null);
      return;
    }
    const session = [...sessions, ...pinnedSessions].find(
      (s) => s.id === activeSession
    );
    if (session) {
      setMessages(normalizeChatHistory(session.chat_history));
      setCurrentSessionName(session.name);
    }
  }, [activeSession, sessions, pinnedSessions]);

  // UI effects
  useEffect(scrollToBottom, [messages, loading]);
  useEffect(resizeTextarea, [input]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
    };
  }, [loadingTimeoutId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const profileContainer = document.querySelector(
        ".profile-dropdown-container"
      );
      if (profileContainer && !profileContainer.contains(event.target)) {
        setIsProfileDropdownVisible(false);
      }
    };

    if (isProfileDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropdownVisible]);

  const handleCourseSelect = (course) => {
    setShowCourseDropdown(false);
    // Save course to localStorage for workspace modes
    localStorage.setItem("currentCourse", JSON.stringify(course));
    navigate(`/sessions/${course.course_number}/${course.course_name}`);
  };

  const handleJoinCourse = () => {
    setShowJoinModal(true);
    setShowCourseDropdown(false);
  };

  const onCourseJoined = () => {
    setShowJoinModal(false);
    setDataLoadingStates((prev) => ({ ...prev, courses: false }));
    setAllCourses([]);
    fetchAllCourses();
  };

  const handleNewSession = () => {
    // If in workspace mode, navigate to default workspace first
    if (workspaceMode) {
      // Set flag to create new session after navigation
      if (typeof window !== "undefined") {
        window.localStorage.setItem(CREATE_NEW_SESSION_FLAG, "true");
      }
      navigate(`/sessions/${courseNumber}/${courseName}`);
    } else {
      // Already in default workspace, create session directly
      handleCreateNewSession();
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selectedAgent) return;
    if (!isSessionVerified) {
      if (!dataLoadingStates.user) {
        handleUnauthorizedAccess();
      }
      return;
    }

    // Only check for critical data that would prevent sending
    if (!user || !selectedAgent?.config_name || !courseName) {
      alert("Unable to send message. Please refresh the page and try again.");
      return;
    }

    // If no active session exists, prevent sending and show message
    let currentSession = [...sessions, ...pinnedSessions].find(
      (s) => s.id === activeSession
    );

    if (!currentSession) {
      currentSession = await ensureSessionExists();
    }

    if (!currentSession) {
      alert("We couldn't start a chat automatically. Please try again.");
      return;
    }

    // Proceed with sending
    await executeSend(text, currentSession);
  };

  // Separated actual send logic so it can be called from modal too
  const executeSend = async (text, currentSession, options = {}) => {
    // Rest of the handleSend function remains the same...
    setInput("");
    setMessages((prev) => [...prev, { type: "query", text }]);
    setLoading(true);

    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 30000);
    setLoadingTimeoutId(timeoutId);

    setTimeout(scrollToBottom, 0);

    try {
      const chatRes = await apiCall("/api/chat", {
        session_id: currentSession.id,
        query: text,
        course_name: courseName,
        assistance_level: assistanceLevel,
        skip_scaffolding: options.skip_scaffolding || false,
      });

      if (!chatRes.ok) {
        const errMsg =
          (chatRes.data && (chatRes.data.message || chatRes.data)) ||
          "Sorry, something went wrong.";
        setMessages((prev) => [...prev, { type: "response", text: errMsg }]);
        return;
      }

      let sessionObj =
        chatRes.data.session || chatRes.data.data || chatRes.data;
      let updatedHistory = Array.isArray(sessionObj?.chat_history)
        ? sessionObj.chat_history
        : [];

      if (updatedHistory.length === 0) {
        const fallbackRes = await apiCall("/api/session/get/one", {
          session_id: sessionObj?._id || currentSession.id, // Use session_id with _id fallback
        });
        if (fallbackRes.ok && fallbackRes.data?.chat_history) {
          updatedHistory = fallbackRes.data.chat_history;
          if (fallbackRes.data._id)
            sessionObj._id = fallbackRes.data._id;
          if (fallbackRes.data.session_name)
            sessionObj.session_name = fallbackRes.data.session_name;
        }
      }

      const newSessionName = sessionObj?.session_name || currentSession.name;
      const sessionId = sessionObj?._id || currentSession.id; // _id is immutable
      const lastMessageTime =
        updatedHistory.length > 0
          ? updatedHistory[updatedHistory.length - 1].date_and_time
          : currentSession.last_message_date;

      // Session ID should never change (it's based on _id now, not session_name)
      // But session_name (display name) can change
      const oldSessionId = activeSession;
      const sessionNameChanged = chatRes.data?.session_name_changed || false;

      setCurrentSessionName(newSessionName);

      // Since we use _id as session ID, it should remain stable
      // No need to update activeSession unless it wasn't set
      if (!oldSessionId || oldSessionId !== sessionId) {
        setActiveSession(sessionId);
        setActiveSessionForCourse(courseName, sessionId);
      }

      setMessages(normalizeChatHistory(updatedHistory));

      // Remove session rename logic - _id doesn't change, only display name can change

      const responseModeRaw = sessionObj?.mode;
      const normalizedResponseMode =
        typeof responseModeRaw === "string"
          ? responseModeRaw.toLowerCase()
          : null;

      const sessionExperimentData =
        sessionObj && typeof sessionObj === "object"
          ? sessionObj.experiment_data
          : null;
      const sessionWorkspaceMode =
        sessionObj?.workspace_mode && typeof sessionObj.workspace_mode === "string"
          ? sessionObj.workspace_mode.toLowerCase()
          : null;

      if (normalizedResponseMode) {
        updateSessionMode(sessionId, normalizedResponseMode);
      }

      const updateSession = (s) => {
        // Match by _id (immutable) instead of session_name
        if (s.id === sessionId || s._id === sessionId || s.id === oldSessionId) {
          const existingMode = resolveSessionMode(s);
          const updated = {
            ...s,
            id: sessionId, // Keep _id as primary identifier
            _id: sessionId, // Ensure _id is set
            name: newSessionName, // Display name can change
            chat_history: updatedHistory,
            last_message_date: lastMessageTime,
          };

          const storedMode = sessionModesRef.current[sessionId];
          const resolvedMode =
            normalizedResponseMode || storedMode || existingMode || null;

          if (resolvedMode && WORKSPACE_MODE_COPY[resolvedMode]) {
            updated.mode = resolvedMode;
          } else if (updated.mode) {
            delete updated.mode;
          }

           if (sessionExperimentData) {
             updated.experiment_data = sessionExperimentData;
           } else if (s.experiment_data) {
             updated.experiment_data = s.experiment_data;
           }

           if (sessionWorkspaceMode) {
             updated.workspace_mode = sessionWorkspaceMode;
           } else if (!updated.workspace_mode && resolvedMode) {
             updated.workspace_mode = resolvedMode;
           }

          return updated;
        }
        return s;
      };

      setSessions((prev) =>
        prev
          .map(updateSession)
          .sort(
            (a, b) =>
              new Date(b.last_message_date) - new Date(a.last_message_date)
          )
      );
      setPinnedSessions((prev) => prev.map(updateSession));

      // Refresh sessions if session name changed
      if (sessionNameChanged) {
        setTimeout(() => {
          if (window.location.pathname.includes(courseName)) {
            setDataLoadingStates((prev) => ({ ...prev, sessions: false }));
            fetchSessions();
          }
        }, 2000);
      }
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          type: "response",
          text: "Unexpected error occurred. Please try again.",
        },
      ]);
    } finally {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        setLoadingTimeoutId(null);
      }
      // Small delay to ensure messages render before loading indicator disappears
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };



  const handleAcceptPracticeQuestion = useCallback(
    async (message, question) => {
      if (!practiceUIEnabled) {
        return;
      }

      const sequenceNum = extractSequenceNumber(message);
      const sequenceKey = sequenceNum !== null ? String(sequenceNum) : "unknown";

      if (sequenceNum === null) {
        setPracticeActionError({
          sequenceKey,
          message: "Unable to capture question metadata. Please try again.",
        });
        return;
      }

      const questionNumber = parseInt(question?.number, 10);
      if (Number.isNaN(questionNumber)) {
        setPracticeActionError({
          sequenceKey,
          message: "Question is missing a number identifier.",
        });
        return;
      }

      const normalizedTextKey = normalizeQuestionText(question?.text);
      const pairKey = `${sequenceNum}-${questionNumber}`;

      // Check if this specific pair (sequence + question number) was already accepted
      if (acceptedQuestionPairs.has(pairKey)) {
        setPracticeActionError({
          sequenceKey,
          message: "That question was already logged for this response.",
        });
        return;
      }

      // Also check by text to prevent duplicates with different numbering
      if (normalizedTextKey && acceptedQuestionTexts.has(normalizedTextKey)) {
        setPracticeActionError({
          sequenceKey,
          message: "That practice question is already saved in this session.",
        });
        return;
      }

      const sessionNameForApi = currentSessionName || activeSessionId;
      if (!sessionNameForApi) {
        setPracticeActionError({
          sequenceKey,
          message: "No active session is available to log this question.",
        });
        return;
      }

      const questionKey = `${sequenceNum}-${questionNumber}`;
      setAcceptingQuestionKey(questionKey);
      setPracticeActionError(null);

      const payload = {
        session_id: sessionNameForApi, // Use session_id instead of session_name
        question_number: questionNumber,
        question_text: question?.text,
        response_sequence_num: sequenceNum,
      };

      if (question?.rawLine) {
        payload.raw_choice_text = question.rawLine;
      }

      const responseIdCandidate =
        message?.response_id ||
        message?.responseId ||
        message?.id ||
        message?.answer_id ||
        message?.answerId ||
        message?.metadata?.response_id ||
        message?.metadata?.responseId;
      if (responseIdCandidate) {
        payload.response_id = responseIdCandidate;
      }

      try {
        const res = await apiCall(
          "/api/session/practice/accept-question",
          payload
        );

        if (!res.ok || res.data?.status !== "success") {
          const serverMessage = res.data?.message;
          throw new Error(serverMessage || "Failed to save practice question.");
        }

        const responseData = res.data?.data || {};
        const acceptedQuestion = responseData.accepted_question || {
          ...payload,
          accepted_at: new Date().toISOString(),
        };
        const practicePhase = responseData.practice_phase || {};

        const updateCollection = (collection) =>
          collection.map((sessionItem) => {
            if (
              sessionItem.id !== sessionNameForApi &&
              sessionItem.name !== sessionNameForApi
            ) {
              return sessionItem;
            }

            const existingExperiment = sessionItem.experiment_data || {};
            const existingAccepted = Array.isArray(
              existingExperiment.accepted_questions
            )
              ? [...existingExperiment.accepted_questions]
              : [];

            const alreadyRecorded = existingAccepted.some((entry) => {
              const entryNum = parseInt(entry?.question_number, 10);
              const entrySeq = parseInt(
                entry?.response_sequence_num ??
                  entry?.sequence_num ??
                  entry?.response_sequence,
                10
              );
              return (
                entryNum === acceptedQuestion.question_number &&
                entrySeq === acceptedQuestion.response_sequence_num &&
                entrySeq !== null
              );
            });

            if (!alreadyRecorded) {
              existingAccepted.push(acceptedQuestion);
            }

            const mergedPracticePhase = {
              ...(existingExperiment.practice_phase || {}),
              ...(practicePhase || {}),
            };

            if (
              practicePhase &&
              typeof practicePhase.questions_accepted_count === "number"
            ) {
              mergedPracticePhase.questions_accepted_count =
                practicePhase.questions_accepted_count;
            } else {
              mergedPracticePhase.questions_accepted_count =
                existingAccepted.length;
            }

            const nextExperiment = {
              ...existingExperiment,
              accepted_questions: existingAccepted,
              practice_phase: mergedPracticePhase,
            };

            if (!nextExperiment.phase) {
              nextExperiment.phase = "practice";
            }

            if (!nextExperiment.condition && practiceCondition) {
              nextExperiment.condition = practiceCondition;
            }

            const nextWorkspaceMode = sessionItem.workspace_mode || "practice";
            const updatedSession = {
              ...sessionItem,
              experiment_data: nextExperiment,
              workspace_mode: nextWorkspaceMode,
            };

            if (
              !sessionItem.mode ||
              (typeof sessionItem.mode === "string" &&
                sessionItem.mode.toLowerCase() !== "practice")
            ) {
              updatedSession.mode = "practice";
            }

            return updatedSession;
          });

        updateSessionMode(sessionNameForApi, "practice");
        setSessions((prev) => updateCollection(prev));
        setPinnedSessions((prev) => updateCollection(prev));
        setPracticeActionError(null);
      } catch (error) {
        setPracticeActionError({
          sequenceKey,
          message: error.message || "Failed to save practice question.",
        });
      } finally {
        setAcceptingQuestionKey(null);
      }
    },
    [
      practiceUIEnabled,
      acceptedQuestionPairs,
      acceptedQuestionTexts,
      apiCall,
      currentSessionName,
      activeSessionId,
      practiceCondition,
      updateSessionMode,
    ]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (loading || isCreatingSession) {
        return;
      }
      handleSend();
    }
  };

  const renderPracticeQuestionControls = useCallback(
    (message) => {
      if (!practiceUIEnabled) return null;

      const parsedQuestions = extractPracticeQuestions(message?.text);
      if (!Array.isArray(parsedQuestions) || parsedQuestions.length === 0) {
        return null;
      }

      const sequenceNum = extractSequenceNumber(message);
      if (sequenceNum === null) {
        return null;
      }

      const sequenceKey = String(sequenceNum);

      return (
        <div className="practice-question-collection" data-sequence={sequenceKey}>
          <div className="practice-question-header">Add to practice log</div>
          {parsedQuestions.map((question) => {
            const questionNumber = parseInt(question.number, 10);
            if (Number.isNaN(questionNumber)) {
              return null;
            }

            const normalizedTextKey = normalizeQuestionText(question.text);
            const questionKey = `${sequenceNum}-${questionNumber}`;
            const isAcceptedByPair = acceptedQuestionPairs.has(questionKey);
            const isAcceptedByText =
              !!normalizedTextKey &&
              acceptedQuestionTexts.has(normalizedTextKey);
            const isAccepted = isAcceptedByPair || isAcceptedByText;
            const isBusy = acceptingQuestionKey === questionKey;

            return (
              <div className="practice-question-item" key={questionKey}>
                <div className="practice-question-index">{questionNumber}.</div>
                <div className="practice-question-text">{question.text}</div>
                <div className="practice-question-actions">
                  {isAccepted ? (
                    <span className="practice-question-status">
                      <FiCheckCircle />
                      Accepted
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAcceptPracticeQuestion(message, question)}
                      disabled={isBusy}
                    >
                      {isBusy ? "Saving..." : "Accept"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {practiceErrorSequenceKey === sequenceKey && practiceErrorMessage ? (
            <div className="practice-question-error">
              {practiceErrorMessage}
            </div>
          ) : null}
        </div>
      );
    },
    [
      practiceUIEnabled,
      acceptedQuestionPairs,
      acceptedQuestionTexts,
      acceptingQuestionKey,
      handleAcceptPracticeQuestion,
      practiceErrorSequenceKey,
      practiceErrorMessage,
    ]
  );

  const handlePinSession = async (sessionToToggle) => {
    const isCurrentlyPinned = pinnedSessions.some(
      (s) => s.id === sessionToToggle.id
    );
    const newPinState = !isCurrentlyPinned;

    if (newPinState && pinnedSessions.length >= 5) {
      alert("You can only pin up to 5 conversations.");
      return;
    }

    const originalPinned = [...pinnedSessions];
    const originalSessions = [...sessions];

    if (newPinState) {
      const sessionToPin = sessions.find((s) => s.id === sessionToToggle.id);
      if (sessionToPin) {
        setPinnedSessions((prev) => [sessionToPin, ...prev]);
        setSessions((prev) => prev.filter((s) => s.id !== sessionToToggle.id));
      }
    } else {
      const sessionToUnpin = pinnedSessions.find(
        (s) => s.id === sessionToToggle.id
      );
      if (sessionToUnpin) {
        setPinnedSessions((prev) =>
          prev.filter((s) => s.id !== sessionToToggle.id)
        );
        setSessions((prev) =>
          [sessionToUnpin, ...prev].sort(
            (a, b) =>
              new Date(b.last_message_date) - new Date(a.last_message_date)
          )
        );
      }
    }

    try {
      const pinRes = await apiCall("/api/session/pin", {
        session_id: sessionToToggle.id, // Use session_id instead of session_name
        is_pinned: newPinState,
      });
      if (!pinRes.ok) throw new Error("Pin API failed");
    } catch (err) {
      console.error("Failed to update pin state:", err);
      setPinnedSessions(originalPinned);
      setSessions(originalSessions);
    }
  };

  const handleDeleteSession = async (sessionToDelete) => {
    if (!sessionToDelete) return;
    const allCurrentSessions = [...sessions, ...pinnedSessions];

    setSessions((prev) => prev.filter((s) => s.id !== sessionToDelete.id));
    setPinnedSessions((prev) =>
      prev.filter((s) => s.id !== sessionToDelete.id)
    );

    if (activeSession === sessionToDelete.id) {
      const remaining = allCurrentSessions.filter(
        (s) => s.id !== sessionToDelete.id
      );
      const newActiveSession = remaining.length > 0 ? remaining[0].id : null;
      setActiveSession(newActiveSession);
      if (newActiveSession) {
        setActiveSessionForCourse(courseName, newActiveSession);
      }
    }

    try {
      const response = await apiCall("/api/session/remove", {
        session_id: sessionToDelete.id, // Use session_id instead of session_name
      });
      if (!response.ok) {
        setSessions(allCurrentSessions.filter((s) => !s.is_pinned));
        setPinnedSessions(allCurrentSessions.filter((s) => s.is_pinned));
        alert(
          `Failed to delete session: ${
            response.data?.message || response.data?.status || "Unknown error"
          }`
        );
      } else {
        clearSessionMode(sessionToDelete.id);
      }
    } catch (err) {
      setSessions(allCurrentSessions.filter((s) => !s.is_pinned));
      setPinnedSessions(allCurrentSessions.filter((s) => s.is_pinned));
      alert("An error occurred while deleting the session.");
    }
  };

  const handleDownloadConversation = async (sessionToDownload) => {
    const session = [...sessions, ...pinnedSessions].find(
      (s) => s.id === sessionToDownload.id
    );
    if (!session || !courseName || !courseNumber) {
      alert("Missing session or course context for download.");
      return;
    }
    const owner =
      session.owner ||
      session.session_owner ||
      user?.username ||
      sessionToDownload.session_owner;
    if (!owner) {
      alert("Unable to resolve session owner for this transcript.");
      return;
    }
    try {
      const response = await fetch(getApiUrl("/api/session/chat/export"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_owner: owner,
          session_id: session.id,
          course_name: courseName,
          course_number: courseNumber,
          student_name: user?.full_name || owner,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Download failed");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const safeName = session.name
        ? session.name.replace(/\s+/g, "_").toLowerCase()
        : session.id;
      anchor.href = url;
      anchor.download = `${safeName}_transcript.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download conversation:", error);
      alert(
        error?.message ||
          "Unable to download this transcript right now. Please try again."
      );
    }
  };

  const isSessionPinned = (session) => {
    return pinnedSessions.some((s) => s.id === session.id);
  };

  const renderSessionListItem = (session, index, prefix) => {
    if (!session) return null;
    const keyBase = session._id || session.id || index;
    const key = `${prefix || "session"}-${keyBase}-${index}`;
    const isActive = activeSession === session.id;
    const sessionMode = resolveSessionMode(session);
    const modeCopy =
      studyFeaturesLoaded &&
      studyFeaturesEnabled &&
      sessionMode &&
      WORKSPACE_MODE_COPY[sessionMode]
        ? WORKSPACE_MODE_COPY[sessionMode]
        : null;
    const ModeIcon = modeCopy ? WORKSPACE_MODE_ICONS[sessionMode] : null;
    
    // Generate display name based on session name
    let displayName = session.name;
    if (session.name) {
      if (session.name.startsWith("New Practice Question Set")) {
        displayName = "New Practice Question Set";
      } else if (session.name.startsWith("New Study Plan")) {
        displayName = "New Study Plan";
      } else if (session.name.startsWith("New Conversation")) {
        displayName = "New Conversation";
      }
    } else {
      displayName = "New Conversation";
    }

    return (
      <div
        key={key}
        className={`recent-chat-item ${isActive ? "active" : ""}`}
        onClick={() => handleSessionSelect(session.id)}
      >
        <div className="recent-chat-info">
          <span className="recent-chat-name" title={displayName}>
            {displayName}
          </span>
          {modeCopy && (
            <span
              className={`session-mode-badge mode-${sessionMode}`}
              title={modeCopy.sessionHint}
            >
              {ModeIcon && (
                <ModeIcon className="session-mode-badge__icon" />
              )}
              {modeCopy.badge}
            </span>
          )}
        </div>
        <SessionMenu
          session={session}
          onPin={handlePinSession}
          onDelete={handleDeleteSession}
          onDownload={handleDownloadConversation}
          onIsPinned={isSessionPinned}
        />
      </div>
    );
  };

  const startWorkspaceNavigation = useCallback(
    (mode, { createNew = false, sessionId = null } = {}) => {
      const normalized =
        typeof mode === "string" ? mode.toLowerCase() : null;
      if (!normalized || (normalized !== "practice" && normalized !== "study")) {
        return;
      }

      // CRITICAL: Save current course to localStorage before navigating to workspace routes
      // This ensures course persists when ChatPage remounts on /student/practice or /student/study
      if (courseName && courseNumber) {
        const courseData = {
          course_name: courseName,
          course_number: courseNumber,
        };
        localStorage.setItem("currentCourse", JSON.stringify(courseData));
      }

      const targetPath =
        normalized === "practice" ? "/student/practice" : "/student/study";
      const alreadyOnTarget = workspaceMode === normalized;

      if (createNew) {
        if (alreadyOnTarget) {
          if (typeof window !== "undefined") {
            window.localStorage.removeItem(CREATE_NEW_SESSION_FLAG);
          }
          handleCreateNewSession();
        } else {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(CREATE_NEW_SESSION_FLAG, "true");
          }
          navigate(targetPath);
        }
        return;
      }

      if (sessionId) {
        setActiveSession(sessionId);
        if (courseName) {
          setActiveSessionForCourse(courseName, sessionId);
        }
      }

      if (!alreadyOnTarget) {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(CREATE_NEW_SESSION_FLAG);
        }
        navigate(targetPath);
      }
    },
    [
      workspaceMode,
      handleCreateNewSession,
      navigate,
      courseName,
      courseNumber,
      setActiveSessionForCourse,
      setActiveSession,
    ]
  );

  const launchWorkspace = useCallback(
    (mode, options = {}) => {
      const { forceNew = false, skipLabCheck = false } = options;
      const normalized =
        typeof mode === "string" ? mode.toLowerCase() : null;
      if (!normalized) {
        return;
      }

      if (!skipLabCheck && !user?.lab_section) {
        setPendingWorkspaceNavigation(normalized);
        setShowLabSectionModal(true);
        return;
      }

      if (!forceNew) {
        const latestSession = getLatestSessionForWorkspace(normalized);
        if (latestSession) {
          setWorkspaceChoice({ mode: normalized, session: latestSession });
          return;
        }
      }

      startWorkspaceNavigation(normalized, { createNew: true });
    },
    [
      user?.lab_section,
      setPendingWorkspaceNavigation,
      setShowLabSectionModal,
      getLatestSessionForWorkspace,
      startWorkspaceNavigation,
    ]
  );

  const handleNavigatePractice = useCallback(() => {
    if (!workspacesUnlocked) {
      setShowUnlockModal(true);
      return;
    }
    launchWorkspace("practice");
  }, [workspacesUnlocked, launchWorkspace]);

  const handleNavigateStudy = useCallback(() => {
    if (!workspacesUnlocked) {
      setShowUnlockModal(true);
      return;
    }
    launchWorkspace("study");
  }, [workspacesUnlocked, launchWorkspace]);

  const handleUnlockSubmit = useCallback(() => {
    if (unlockInput.trim().toUpperCase() === UNLOCK_CODE) {
      setWorkspacesUnlocked(true);
      localStorage.setItem("mq_workspaces_unlocked", "true");
      setShowUnlockModal(false);
      setUnlockInput("");
      setUnlockError("");
    } else {
      setUnlockError("Invalid code. Please try again.");
    }
  }, [unlockInput, UNLOCK_CODE]);

  const handleUnlockCancel = useCallback(() => {
    setShowUnlockModal(false);
    setUnlockInput("");
    setUnlockError("");
  }, []);

  const handleLabSectionAssigned = useCallback(
    (updatedUser) => {
      setShowLabSectionModal(false);

      if (pendingWorkspaceNavigation) {
        const targetMode = pendingWorkspaceNavigation;
        setPendingWorkspaceNavigation(null);
        launchWorkspace(targetMode, { skipLabCheck: true });
      }
    },
    [pendingWorkspaceNavigation, launchWorkspace]
  );

  const handleLabSectionModalClose = useCallback(() => {
    setShowLabSectionModal(false);
    setPendingWorkspaceNavigation(null);
  }, []);

  const handleWorkspaceChoiceCancel = useCallback(() => {
    setWorkspaceChoice(null);
  }, []);

  const handleWorkspaceChoiceContinue = useCallback(() => {
    setWorkspaceChoice((current) => {
      if (!current || !current.mode) {
        return null;
      }
      const sessionId = current.session?.id || current.session?.name || null;
      startWorkspaceNavigation(current.mode, {
        sessionId,
      });
      return null;
    });
  }, [startWorkspaceNavigation]);

  const handleWorkspaceChoiceNew = useCallback(() => {
    setWorkspaceChoice((current) => {
      if (!current || !current.mode) {
        return null;
      }
      startWorkspaceNavigation(current.mode, { createNew: true });
      return null;
    });
  }, [startWorkspaceNavigation]);

  const handleAssistanceLevelChange = (newLevel) => {
    setAssistanceLevel(newLevel);
    // Persist to localStorage
    if (courseName) {
      try {
        localStorage.setItem(`mq_assistance_level_${courseName}`, String(newLevel));
      } catch (e) {
        console.warn("Failed to save assistance level", e);
      }
    }
  };

  // Create session if none exist and user tries to interact
  const ensureSessionExists = useCallback(async () => {
    const allSessions = [...pinnedSessions, ...sessions];

    if (activeSession) {
      const existingActive = allSessions.find(
        (sessionItem) => sessionItem.id === activeSession
      );
      if (existingActive) {
        return existingActive;
      }
    }

    if (allSessions.length > 0) {
      const fallbackSession = allSessions[0];
      setActiveSession(fallbackSession.id);
      if (courseName) {
        setActiveSessionForCourse(courseName, fallbackSession.id);
      }
      return fallbackSession;
    }

    if (
      isCreatingSession ||
      !user ||
      !courseName ||
      !selectedAgent
    ) {
      return null;
    }

    return await handleCreateNewSession(true);
  }, [
    pinnedSessions,
    sessions,
    activeSession,
    setActiveSession,
    courseName,
    setActiveSessionForCourse,
    isCreatingSession,
    user,
    selectedAgent,
    handleCreateNewSession,
  ]);

  // Check for sessions when user and agent are ready
  useEffect(() => {
    if (
      !sessionsInitialized ||
      isCreatingSession ||
      dataLoadingStates.sessions ||
      !user ||
      !courseName ||
      !selectedAgent
    ) {
      return;
    }

    if (sessions.length === 0 && pinnedSessions.length === 0) {
      ensureSessionExists();
    }
  }, [
    sessionsInitialized,
    isCreatingSession,
    dataLoadingStates.sessions,
    user,
    courseName,
    selectedAgent,
    sessions.length,
    pinnedSessions.length,
    ensureSessionExists,
  ]);

  return (
    <div
      className={`claude-container ${
        isSidebarCollapsed ? "sidebar-collapsed" : ""
      }`}
    >
      <button
        className="mobile-sidebar-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setIsSidebarCollapsed((c) => !c);
        }}
      >
        <FiMenu />
      </button>

      <aside className="claude-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="sidebar-top">
          <div className="sidebar-header">
            {!isSidebarCollapsed && (
              <div className="sidebar-logo">
                <img src="/merryquery-logo.png" alt="MerryQuery" />
                <span>MerryQuery</span>
              </div>
            )}
            <button
              className="sidebar-toggle"
              onClick={() => setIsSidebarCollapsed((c) => !c)}
            >
              {isSidebarCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            </button>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item new-chat-button ${!workspaceMode && !activeSessionMode ? "active" : ""}`}
              onClick={handleNewSession}
              disabled={isCreatingSession || !selectedAgent}
            >
              <FiPlus />
              {!isSidebarCollapsed && <span>New Chat</span>}
            </button>

            {user?.role === "student" && studyFeaturesLoaded && studyFeaturesEnabled && (
              <div className="workspace-mode-switcher">
                <button
                  type="button"
                  className={`nav-item workspace-mode-button ${
                    workspaceMode === "practice" ? "active" : ""
                  } ${!workspacesUnlocked ? "locked" : ""}`}
                  onClick={handleNavigatePractice}
                  title={workspacesUnlocked ? "Practice Questions" : "Unlock to access Practice Questions"}
                >
                  <FiTarget />
                  {!isSidebarCollapsed && (
                    <>
                      <span>Practice Questions</span>
                      {!workspacesUnlocked && <span className="lock-icon">🔒</span>}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={`nav-item workspace-mode-button ${
                    workspaceMode === "study" ? "active" : ""
                  } ${!workspacesUnlocked ? "locked" : ""}`}
                  onClick={handleNavigateStudy}
                  title={workspacesUnlocked ? "Study Coach" : "Unlock to access Study Coach"}
                >
                  <FiBookOpen />
                  {!isSidebarCollapsed && (
                    <>
                      <span>Study Coach</span>
                      {!workspacesUnlocked && <span className="lock-icon">🔒</span>}
                    </>
                  )}
                </button>
              </div>
            )}

            <div
              className="nav-item clickable-nav-item"
              onClick={() => setIsSearching(!isSearching)}
            >
              <FiMessageSquare />
              {!isSidebarCollapsed && <span>Search Chats</span>}
            </div>

            <div className="recent-chats-list">
              {!isSidebarCollapsed && pinnedSessions.length > 0 && (
                <h3 className="recents-title">Pinned</h3>
              )}
              {pinnedSessions.map((s, index) =>
                renderSessionListItem(s, index, "pinned")
              )}

              {!isSidebarCollapsed && recentSessions.length > 0 && (
                <h3 className="recents-title">Recents</h3>
              )}
              {recentSessions
                .filter((s) => !isSessionPinned(s))
                .map((s, index) =>
                  renderSessionListItem(s, index, "recent")
                )}

              {!isSidebarCollapsed && olderSessions.length > 0 && (
                <h3 className="recents-title">More than a week ago</h3>
              )}
              {olderSessions
                .filter((s) => !isSessionPinned(s))
                .map((s, index) =>
                  renderSessionListItem(s, index, "older")
                )}
            </div>
          </nav>

          {!isSidebarCollapsed && isSearching && (
            <div className="search-container">
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          )}
        </div>
        <div className="sidebar-bottom">
          {user &&
            (
            (
              <div  
                className="user-profile profile-dropdown-container"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsProfileDropdownVisible((prev) => !prev);
                }}
              >
                <div className="user-avatar">
                  <FiUser />
                </div>
                {!isSidebarCollapsed && (
                  <div className="user-details">
                    <span className="user-name">{user.full_name}</span>
                  </div>
                )}
                {isProfileDropdownVisible && (
                  <div
                    className="profile-dropdown-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="profile-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSettingsModal(true);
                        setIsProfileDropdownVisible(false);
                      }}
                    >
                      <FiSettings />
                      Settings
                    </div>
                    <div className="profile-dropdown-divider" />
                    <div
                      className="profile-dropdown-item logout"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsProfileDropdownVisible(false);
                        logout();
                      }}
                    >
                      Log out
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      </aside>

      <main className="claude-main">
        <div className="main-header">
          <div className="header-left">
            <div
              className="course-selector"
              onMouseEnter={() => {
                if (courseDropdownHideTimeout.current)
                  clearTimeout(courseDropdownHideTimeout.current);
                setShowCourseDropdown(true);
              }}
              onMouseLeave={() => {
                courseDropdownHideTimeout.current = setTimeout(() => {
                  setShowCourseDropdown(false);
                  setIsShowingCourseSettings(false);
                }, 180);
              }}
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setShowCourseDropdown((v) => !v);
                }
              }}
            >
              <FiGrid />
              <span>
                {courseName ? decodeURIComponent(courseName) : "Select Course"}
              </span>

              {showCourseDropdown && (
                <div
                  className="course-dropdown"
                  onMouseEnter={() => {
                    if (courseDropdownHideTimeout.current)
                      clearTimeout(courseDropdownHideTimeout.current);
                    setShowCourseDropdown(true);
                  }}
                  onMouseLeave={() => {
                    courseDropdownHideTimeout.current = setTimeout(() => {
                      setShowCourseDropdown(false);
                      setIsShowingCourseSettings(false);
                    }, 180);
                  }}
                >
                  <>
                    <div className="dropdown-header">Your Courses</div>
                    <div className="dropdown-item current-course">
                      <span>{decodeURIComponent(courseName || "")}</span>
                    </div>
                    <div className="dropdown-divider" />
                    {allCourses
                      .filter(
                        (course) =>
                          course.course_name !==
                          decodeURIComponent(courseName || "")
                      )
                      .map((course) => (
                        <div
                          key={course._id}
                          className="dropdown-item"
                          onClick={() => handleCourseSelect(course)}
                        >
                          {course.course_name}
                        </div>
                      ))}
                    <div className="dropdown-divider" />
                    <div
                      className="dropdown-item join-new"
                      onClick={handleJoinCourse}
                    >
                      <FiPlus />
                      Join New Course
                    </div>
                  </>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="chat-area"
          onClick={() => {
            if (window.innerWidth <= 768) {
              setIsSidebarCollapsed(true);
            }
          }}
        >
          <div className="chat-window" ref={chatWindowRef}>
            {messages.length === 0 && !loading && (
              <div className="welcome-message">
                <img
                  src="/merryquery-logo.png"
                  alt="Welcome"
                  className="welcome-logo"
                />
                <h2>Hey there!</h2>
                <p>Ask me anything about your course to start chatting 😊</p>
                {(dataLoadingStates.user ||
                  dataLoadingStates.courses ||
                  dataLoadingStates.agent ||
                  dataLoadingStates.sessions) && (
                  <p className="loading-indicator">Loading your data...</p>
                )}
              </div>
            )}

            {messages.map((m, i) => {
              const isResponse = m.type !== "query";

              let pairedQuery = null;
              if (isResponse) {
                if (m.sequenceNum !== null && m.sequenceNum !== undefined) {
                  pairedQuery = messages.find(
                    (candidate) =>
                      candidate.type === "query" &&
                      candidate.sequenceNum === m.sequenceNum
                  );
                }

                if (!pairedQuery) {
                  for (let j = i - 1; j >= 0; j -= 1) {
                    const candidate = messages[j];
                    if (!candidate) {
                      continue;
                    }
                    if (candidate.type === "query") {
                      pairedQuery = candidate;
                      break;
                    }
                    if (candidate.type === "response") {
                      break;
                    }
                  }
                }
              }

              const qualityScore =
                typeof m.scaffolding?.quality_score === "number"
                  ? m.scaffolding.quality_score
                  : null;

              return (
                <div
                  key={i}
                  className={`message-wrapper ${
                    m.type === "query" ? "user" : "ai"
                  }`}
                >
                  <div
                    className={`message-bubble ${
                      m.type === "query" ? "user-message" : "ai-message"
                    }`}
                  >
                    {m.type === "query" ? (
                      m.text
                    ) : (
                      <>
                        {/* Show scaffolding coach (tips + praise) before answer if available */}
                        {studyFeaturesEnabled &&
                          (m.scaffolding?.suggestions?.length > 0 ||
                            m.scaffolding?.praise) && (
                          <ScaffoldingCoach
                            scaffolding={{
                              suggestions: m.scaffolding.suggestions || [],
                              categories: m.scaffolding.categories || [],
                              quality_score: qualityScore,
                              praise: m.scaffolding.praise,
                              reflection_prompts: [],
                            }}
                          />
                        )}

                        {/* Show answer */}
                        <MarkdownRenderer>{m.text}</MarkdownRenderer>

                        {/* Show evaluation prompts after answer if available */}
                        {studyFeaturesEnabled &&
                          m.scaffolding?.reflection_prompts?.length > 0 && (
                          <ScaffoldingCoach
                            scaffolding={{
                              suggestions: [],
                              reflection_prompts: m.scaffolding.reflection_prompts,
                              quality_score: qualityScore,
                              categories: [],
                            }}
                          />
                        )}

                        {renderPracticeQuestionControls(m)}
                        <MessageFeedback
                          message={m}
                          sessionId={activeSession}
                          messageIndex={i}
                          responseId={m.id || `msg_${i}`}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="message-wrapper ai">
                <div className="message-bubble ai-message">
                  <ThinkingIndicator message={thinkingMessage} />
                </div>
              </div>
            )}
          </div>

          <div className="chat-input-area">
            {(showPromptGuideAccess || showAssistanceLevel) && (
              <div
                className={`chat-input-top-row${
                  showPromptGuideAccess ? " has-prompt-guide" : ""
                }${showAssistanceLevel ? " has-assistance-level" : ""}`}
              >
                {showPromptGuideAccess && (
                  <button
                    type="button"
                    className="prompt-guide-button"
                    onClick={handlePromptGuideButtonClick}
                  >
                    <FiBook />
                    <span>Prompt Guide</span>
                  </button>
                )}
                {showAssistanceLevel && (
                  <div className="assistance-level-wrapper">
                    <AssistanceLevelSelector
                      selectedLevel={assistanceLevel}
                      onLevelChange={handleAssistanceLevelChange}
                      loading={loading}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="chat-input-bar">
              <textarea
                ref={inputRef}
                rows={1}
                placeholder={
                  !selectedAgent
                    ? "Loading agent configuration..."
                    : sessions.length === 0 && pinnedSessions.length === 0
                    ? "Ask anything about your course to start chatting"
                    : !activeSession
                    ? "Ask anything to start a new conversation"
                    : "Ask MerryQuery"
                }
                className="chat-input"
                value={input}
                disabled={loading || !selectedAgent}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={
                  loading ||
                  !input.trim() ||
                  !selectedAgent ||
                  isCreatingSession
                }
              >
                <FiSend />
              </button>
            </div>
            <div className="chat-disclaimer">
              MerryQuery incorporates LLM APIs. Please verify important
              information and don't share personal data!
            </div>
          </div>
        </div>
      </main>

      {showJoinModal && (
        <JoinCourseModal
          isVisible={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          onSuccess={onCourseJoined}
        />
      )}

      {showPromptGuideAccess && (
        <PromptGuideModal
          isOpen={showPromptGuideModal}
          workspaceMode={promptGuideMode}
          onClose={handlePromptGuideClose}
          onOpenGuide={handlePromptGuideOpenDoc}
          guideUrl={promptGuideUrl}
        />
      )}

      {workspaceChoice && (
        <WorkspaceChoiceModal
          isOpen={Boolean(workspaceChoice)}
          workspaceMode={workspaceChoice?.mode}
          lastSession={workspaceChoice?.session}
          onClose={handleWorkspaceChoiceCancel}
          onContinue={handleWorkspaceChoiceContinue}
          onStartNew={handleWorkspaceChoiceNew}
        />
      )}

      {showLabSectionModal && (
        <LabSectionModal
          isOpen={showLabSectionModal}
          onClose={handleLabSectionModalClose}
          onAssigned={handleLabSectionAssigned}
          forceSelection={false}
        />
      )}

      {showUnlockModal && (
        <div className="modal-overlay" onClick={handleUnlockCancel}>
          <div className="modal-content unlock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔓 Unlock Study Features</h2>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#555' }}>
                Enter the access code provided by your instructor to unlock the Practice Questions and Study Coach workspaces.
              </p>
              <input
                type="text"
                className="unlock-input"
                placeholder="Enter unlock code"
                value={unlockInput}
                onChange={(e) => {
                  setUnlockInput(e.target.value);
                  setUnlockError("");
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleUnlockSubmit();
                  }
                }}
                autoFocus
              />
              {unlockError && (
                <div className="unlock-error">{unlockError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button secondary" onClick={handleUnlockCancel}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleUnlockSubmit}>
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <SettingsModal
          isVisible={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          user={user}
        />
      )}
    </div>
  );
};

export default ChatPage;
